import { useCallback } from 'react'
import { useLocalStorage } from '@/hooks/use-local-storage'
import type { Product } from '@/lib/types'

interface ViewHistory {
  productId: string
  timestamp: number
  category?: string
  brand?: string
}

interface InteractionHistory {
  productId: string
  type: 'view' | 'cart' | 'quote' | 'compare'
  timestamp: number
  category?: string
  brand?: string
}

export function useRecommendationTracking() {
  const [, setViewHistory] = useLocalStorage<ViewHistory[]>('view-history', [])
  const [, setInteractionHistory] = useLocalStorage<InteractionHistory[]>('interaction-history', [])

  const trackInteraction = useCallback((
    productId: string,
    type: InteractionHistory['type'],
    category?: string,
    brand?: string
  ) => {
    const newInteraction: InteractionHistory = {
      productId,
      type,
      timestamp: Date.now(),
      category,
      brand,
    }

    setInteractionHistory((current) => {
      const filtered = (current || []).filter(
        (interaction) => !(interaction.productId === productId && interaction.type === type)
      )
      return [newInteraction, ...filtered].slice(0, 100)
    })
  }, [setInteractionHistory])

  const trackView = useCallback((product: Product) => {
    const newView: ViewHistory = {
      productId: product.id,
      timestamp: Date.now(),
      category: product.category,
      brand: product.brand,
    }

    setViewHistory((current) => {
      const filtered = (current || []).filter((view) => view.productId !== product.id)
      return [newView, ...filtered].slice(0, 50)
    })

    trackInteraction(product.id, 'view', product.category, product.brand)
  }, [setViewHistory, trackInteraction])

  return {
    trackInteraction,
    trackView,
  }
}
